/************************************** THIRD-PARTY MODULES ***************************************/
import React from 'react';
import ReactDOM from 'react-dom';
import {getDisplayName, isClassComponent} from 'recompose';

import {Injection} from 'mad-utils/lib/react';

/**************************************** TYPE DEFINITIONS ****************************************/
export interface LaddaProps {
    isLoading?: Injection<boolean>;
    is2ndPartLoading?: Injection<boolean>;
    laddaOn?: Injection<() => void>;
    laddaOff?: Injection<() => void>;
    ladda2On?: Injection<() => void>;
    ladda2Off?: Injection<() => void>;
    allLaddasOn?: Injection<() => void>;
    allLaddasOff?: Injection<() => void>;
}

/********************************************* EXPORT *********************************************/
/**
 * Adds ladda controller and state props to the component.
 *
 * Added "state" props:
 *     isLoading: boolean
 *         Set up so that if this is true, ladda shows up in 1st (or only) possible location.
 *     is2ndPartLoading: boolean
 *         Set up so that if this is true, ladda shows up in 2nd possible location.
 *
 * Added "method" props:
 *     laddaOn: EventHandler
 *         Sets isLoading to true
 *     laddaOff: EventHandler
 *         Sets isLoading to false
 *     ladda2On: EventHandler
 *         Sets is2ndPartLoading to true
 *     ladda2Off: EventHandler
 *         Sets is2ndPartLoading to false
 *     allLaddasOn: EventHandler
 *         Sets both isLoading and is2ndPartLoading to true
 *     allLaddasOff: EventHandler
 *         Sets both isLoading and is2ndPartLoading to false
 */
export const asLaddaController = (WrappedComponent): any => {
    const retClass = class extends React.Component<any, any> {
        state = {
            isLoading: false,
            is2ndPartLoading: false,
        };

        /**
         * Change container state to activate 1st (or only) ladda inside rendered view.
         */
        laddaOn = () => this.setState({isLoading: true});

        /**
         * Change container state to deactivate 1st (or only) ladda inside rendered view.
         */
        laddaOff = () => this.setState({isLoading: false});

        /**
         * Change container state to activate 2nd ladda inside rendered view.
         */
        ladda2On = () => this.setState({is2ndPartLoading: true});

        /**
         * Change container state to deactivate 2nd ladda inside rendered view.
         */
        ladda2Off = () => this.setState({is2ndPartLoading: false});

        /**
         * Change container state to activate all laddas inside rendered view.
         */
        allLaddasOn = () => {
            this.laddaOn();
            this.ladda2On();
        };

        /**
         * Change container state to deactivate all laddas inside rendered view.
         */
        allLaddasOff = () => {
            this.laddaOff();
            this.ladda2Off();
        };

        render() {
            return (
                <WrappedComponent
                    {...this.props}
                    {...this.state}
                    laddaOn={this.laddaOn}
                    laddaOff={this.laddaOff}
                    ladda2On={this.ladda2On}
                    ladda2Off={this.ladda2Off}
                    allLaddasOn={this.allLaddasOn}
                    allLaddasOff={this.allLaddasOff}
                />
            );
        }
    };

    retClass.displayName = `asLaddaController(${getDisplayName(WrappedComponent)})`;

    return retClass;
};
